import { z } from 'zod/v4'
import { WorkflowNodeBaseSchema } from '../../../resources/types'

/**
 * Converts empty/whitespace-only strings to undefined (handles Liquid template outputs),
 * then validates that any remaining string has content.
 */
const optionalNonBlankString = z
  .string()
  .optional()
  .transform((v) => {
    if (v === undefined || v === null) return undefined
    const trimmed = v.trim()
    return trimmed.length === 0 ? undefined : v
  })

export const ThreadCreateInputsSchema = z
  .object({
    id: optionalNonBlankString.describe(
      'Existing thread ID to use instead of creating a new thread',
    ),
    title: z.string().optional().describe('Title of the thread'),
    kind: z
      .enum(['CONVERSATION', 'INTERNAL', 'ESCALATION', 'BROADCAST'])
      .optional()
      .describe('Kind of thread'),
    source: z
      .object({
        'thread-id': optionalNonBlankString.describe(
          'Source thread ID to create a child thread from',
        ),
        'message-id': optionalNonBlankString.describe(
          'Message ID that triggered this sub-thread (for visual anchoring in UI)',
        ),
      })
      .optional()
      .describe('Source thread metadata for linking'),
    'inherit-participants-from-source': z
      .boolean()
      .optional()
      .describe('Whether to inherit participants from source thread'),
    participants: z
      .array(
        z.object({
          // Contact participant
          'contact-id': optionalNonBlankString.describe(
            'Contact ID to add as participant',
          ),
          // Member participant
          'member-id': optionalNonBlankString.describe(
            'Member ID to add as participant',
          ),
          // Agent participant (by ID or handle)
          'agent-id': optionalNonBlankString.describe(
            'Agent ID to add as participant',
          ),
          'agent-handle': optionalNonBlankString.describe(
            'Agent handle to add as participant',
          ),
          // Workflow participant (by ID or handle)
          'workflow-id': optionalNonBlankString.describe(
            'Workflow ID to add as participant',
          ),
          'workflow-handle': optionalNonBlankString.describe(
            'Workflow handle to add as participant',
          ),
          // Direct participant reference
          'participant-id': optionalNonBlankString.describe(
            'Participant ID to add directly',
          ),
          role: z
            .enum(['OWNER', 'PARTICIPANT', 'OBSERVER'])
            .optional()
            .describe('Role of the participant (defaults to PARTICIPANT)'),
          'identifier-value': optionalNonBlankString.describe(
            'Creates CommunicationChannelSubscription for this participant',
          ),
        }),
      )
      .optional()
      .describe(
        'Participants to add to the thread (contacts, members, agents, workflows)',
      ),
    'communication-channel-id': optionalNonBlankString.describe(
      'Communication channel for this thread (immutable after creation)',
    ),
    'routing-address': optionalNonBlankString.describe(
      'Routing address (e.g., phone number, email, WhatsApp group ID) - immutable after creation',
    ),
    /** @deprecated Use routing-address */
    'routing-target': optionalNonBlankString.describe(
      'Deprecated alias for routing-address',
    ),
    'routing-contact-id': optionalNonBlankString.describe(
      'Contact ID to set as the routing participant for 1:1 conversations - immutable after creation',
    ),
    'external-id': optionalNonBlankString.describe(
      'External system conversation/group ID',
    ),
    context: z
      .array(
        z.object({
          kind: z.enum(['INSTANCE', 'THREAD', 'DOCUMENT']),
          handle: z.string(),
          'instance-id': optionalNonBlankString,
          'linked-thread-id': optionalNonBlankString,
          'document-url': optionalNonBlankString,
          role: z
            .enum(['PRIMARY', 'RELEVANT', 'MENTIONED', 'HISTORICAL'])
            .optional(),
          reason: z.string().optional(),
        }),
      )
      .optional()
      .describe('Context entries to attach to the thread'),
  })
  .superRefine((data, ctx) => {
    const kind = data.kind ?? 'CONVERSATION'
    const channel = data['communication-channel-id']?.trim()
    const routing =
      data['routing-address']?.trim() ||
      data['routing-target']?.trim() ||
      '' ||
      undefined

    if (
      kind === 'CONVERSATION' &&
      channel &&
      channel.length > 0 &&
      (!routing || routing.length === 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'routing-address (or deprecated routing-target) is required when communication-channel-id is set for CONVERSATION threads',
        path: ['routing-address'],
      })
    }

    if (
      data.participants?.length &&
      data.participants.some((p) => {
        const idv = p['identifier-value']?.trim()
        return Boolean(idv && idv.length > 0)
      })
    ) {
      if (!channel || channel.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message:
            'communication-channel-id is required when a participant includes identifier-value',
          path: ['communication-channel-id'],
        })
      }
    }

    data.context?.forEach((c, i) => {
      if (c.kind === 'INSTANCE' && !c['instance-id']?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'INSTANCE context requires instance-id',
          path: ['context', i, 'instance-id'],
        })
      }
      if (c.kind === 'THREAD' && !c['linked-thread-id']?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'THREAD context requires linked-thread-id',
          path: ['context', i, 'linked-thread-id'],
        })
      }
      if (c.kind === 'DOCUMENT' && !c['document-url']?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'DOCUMENT context requires document-url',
          path: ['context', i, 'document-url'],
        })
      }
    })
  })

const ThreadCreateOptionsSchema = z.object({})

export const ThreadCreateCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/threads'),
    cmd: z.literal('thread.create'),
    options: ThreadCreateOptionsSchema.optional(),
  }),
  inputs: ThreadCreateInputsSchema,
})

export type ThreadCreateCommand = z.infer<typeof ThreadCreateCommandSchema>
